/**
 * Shared fare calculation for search, details, passengers, and payment.
 * Fare/Pax → flight.display_price (API). Totals → commission included via display_total_fare or applyCommission(base).
 */

function parseAmount(value) {
  const parsed = parseFloat(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function applyFlightCommission(base, commissionFeePercent) {
  const amount = parseAmount(base);
  const fee = parseAmount(commissionFeePercent);
  if (amount <= 0) return 0;
  if (fee <= 0) return Math.round(amount);
  return Math.round(amount + (amount * fee) / 100);
}

export function isBELFlight(flight) {
  if (!flight) return false;
  return (
    flight.is_bel_flight ||
    (flight.block_empty_price &&
      parseFloat(flight.block_empty_price) > 0 &&
      (!flight.seat_price || parseFloat(flight.seat_price) === 0))
  );
}

function hasSeatPricing(flight) {
  if (!flight || isBELFlight(flight)) return false;
  return !!(flight.block_price && flight.seat_price && parseFloat(flight.seat_price) > 0);
}

/** Fare/Pax label value — API display_price, then search price, then computed fallback */
export function getFlightDisplayFarePerPax(flight) {
  if (!flight) return 0;

  const displayPrice = parseAmount(flight.display_price);
  if (displayPrice > 0) return displayPrice;

  const searchPrice = parseAmount(flight.price);
  if (searchPrice > 0) return searchPrice;

  if (hasSeatPricing(flight)) {
    return applyFlightCommission(flight.seat_price, flight.commission_fee);
  }

  return applyFlightCommission(flight.block_empty_price, flight.commission_fee);
}

/** Line total for a leg — commission included */
export function getFlightDisplayTotalFare(flight, passengers = 1) {
  if (!flight) return 0;

  const pax = Math.max(1, parseInt(passengers, 10) || 1);
  const displayTotal = parseAmount(flight.display_total_fare);
  if (displayTotal > 0) return displayTotal;

  const commission = flight.commission_fee;
  const farePerPax = getFlightDisplayFarePerPax(flight);

  if (hasSeatPricing(flight)) {
    const seatBase = parseAmount(flight.seat_price);
    const commissionedPerPax = applyFlightCommission(seatBase, commission);

    if (parseAmount(flight.display_price) > 0) {
      // display_price is Fare/Pax; if it already includes commission, scale by passengers
      if (
        Math.abs(farePerPax - commissionedPerPax) < 1 ||
        farePerPax >= commissionedPerPax
      ) {
        return Math.round(farePerPax * pax);
      }
      return applyFlightCommission(seatBase * pax, commission);
    }

    return applyFlightCommission(seatBase * pax, commission);
  }

  if (isBELFlight(flight)) {
    if (parseAmount(flight.display_price) > 0) {
      return parseAmount(flight.display_price);
    }
    return applyFlightCommission(flight.block_empty_price, commission);
  }

  return farePerPax > 0 ? Math.round(farePerPax * pax) : 0;
}

export function calculateFlightPricing(flight, passengers = 1) {
  if (!flight) {
    return { farePerPax: 0, totalFare: 0, blockFare: 0, isSeatPricing: false };
  }

  const isSeatPricing = hasSeatPricing(flight);
  const farePerPax = getFlightDisplayFarePerPax(flight);
  const totalFare = getFlightDisplayTotalFare(flight, passengers);
  const blockFare = isSeatPricing ? 0 : totalFare;

  return {
    farePerPax,
    totalFare,
    blockFare,
    isSeatPricing,
  };
}

export function calculateFlightFarePerPax(flight) {
  return getFlightDisplayFarePerPax(flight);
}

export function calculateFlightLegTotal(flight, passengers = 1) {
  return getFlightDisplayTotalFare(flight, passengers);
}

export function getFlightDepartureDateTime(flight) {
  return flight?.departure?.datetime || flight?.departure_date_time || null;
}

export function getFlightArrivalDateTime(flight) {
  return flight?.arrival?.datetime || flight?.arrival_date_time || null;
}

export function getFlightDurationMinutes(flight) {
  if (!flight) return { hours: 0, minutes: 0 };

  const raw = flight.duration_minutes;
  if (raw != null && !Number.isNaN(Number(raw))) {
    const total = Number(raw);
    return { hours: Math.floor(total / 60), minutes: total % 60 };
  }

  const dep = getFlightDepartureDateTime(flight);
  const arr = getFlightArrivalDateTime(flight);
  if (!dep || !arr) return { hours: 0, minutes: 0 };

  const diffMs = new Date(arr).getTime() - new Date(dep).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return { hours: 0, minutes: 0 };

  const totalMinutes = Math.floor(diffMs / 60000);
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

export function formatFlightDurationLabel(flight) {
  const { hours, minutes } = getFlightDurationMinutes(flight);
  return `${hours} Hrs ${minutes} Mins`;
}
